/**
 * Context provider allowing child components
 * to override main template UI state.
 */

import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useState,
} from 'react';

type TMainTemplateContext = {
  isNavigationHidden: boolean;
  setIsNavigationHidden: (value: boolean) => void;
  isForeignContentNotificationHidden: boolean;
  setIsForeignContentNotificationHidden: (value: boolean) => void;
};

const MainTemplateContext = createContext<TMainTemplateContext>({
  /** If true, main navigation should be hidden, regardless of other state */
  isNavigationHidden: false,
  setIsNavigationHidden: () => {
    throw new Error('Not implemented');
  },

  /** If true, foreign content notification should be hidden, regardless of other state */
  isForeignContentNotificationHidden: false,
  setIsForeignContentNotificationHidden: () => {
    throw new Error('Not implemented');
  },
});

export const MainTemplateContextProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [isNavigationHidden, setIsNavigationHidden] = useState(false);
  const [
    isForeignContentNotificationHidden,
    setIsForeignContentNotificationHidden,
  ] = useState(false);

  return (
    <MainTemplateContext.Provider
      value={{
        isNavigationHidden,
        setIsNavigationHidden,
        isForeignContentNotificationHidden,
        setIsForeignContentNotificationHidden,
      }}
    >
      {children}
    </MainTemplateContext.Provider>
  );
};

export const useMainTemplateContext = () => {
  return useContext(MainTemplateContext);
};

export default MainTemplateContext;
